"use client"

import { useState } from "react"
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { LinksDetailsModal } from "@/components/links-details-modal"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { ComboConfigurationCell } from "@/components/combo-configuration-cell"
import { TableSkeleton } from "@/components/table-skeleton"
import {
  AlertCircle,
  Check,
  ExternalLink,
  Eye,
  Files,
  Pencil,
  Repeat,
  X,
} from "lucide-react"
import type { ApiOrderLike } from "@/lib/order-table-utils"
import {
  formatOrderTableDate,
  formatOrderDueDate,
  getRecurrenceDisplay,
  safeNumericValue,
  safeStringValue,
  calculateProgress,
  getClientOrderStatusColor,
  formatClientOrderStatus,
  getDateStatus,
  getOrderLocations,
  getOrderCombos,
  getLinkStatuses,
  getLinksProgressCounts,
  isConnectionDeleted,
  getClientName,
  getBusinessName,
  getClientId,
  getBusinessId,
  getOrderLinkedUsersEmails,
} from "@/lib/order-table-utils"

export interface OrdersTableColumn {
  id: string
  label: string
  isVisible: boolean
  isAlwaysVisible?: boolean
}

interface OrdersTableProps {
  orders: ApiOrderLike[]
  columns: OrdersTableColumn[]
  hideClientColumn?: boolean
  /** Base path for order detail links (default internal CRM `/orders`). Client dashboard uses `/client/orders`. */
  orderDetailBasePath?: string
  isLoading?: boolean
  paginationLoading?: boolean
  error?: string
  onRetry?: () => void
  emptyMessage?: string
  /** When provided, a "Duplicate order" action is shown per row (ZS-154). */
  onDuplicateOrder?: (order: ApiOrderLike) => void
}

export function OrdersTable({
  orders,
  columns,
  hideClientColumn = false,
  orderDetailBasePath = "/orders",
  isLoading = false,
  paginationLoading = false,
  error,
  onRetry,
  emptyMessage = "No orders found matching your criteria",
  onDuplicateOrder,
}: OrdersTableProps) {
  const [linksModalOrder, setLinksModalOrder] = useState<ApiOrderLike | null>(null)
  const visibleColumns = columns.filter((col) => col.isVisible && (col.id !== "client" || !hideClientColumn))
  const columnCount = visibleColumns.length
  const userEmailColumn = columns.find((col) => col.id === "user_email")
  const orderBase = orderDetailBasePath.replace(/\/$/, "")
  const orderDetailHref = (orderId: string) => `${orderBase}/${orderId}`
  const showInternalOrderEdit = orderBase === "/orders"

  function getActiveConnections(order: ApiOrderLike): any[] {
    const conns = order?.connections
    if (!conns || !Array.isArray(conns)) return []
    return conns.filter((c: any) => !isConnectionDeleted(c))
  }

  return (
    <>
    <TooltipProvider delayDuration={300}>
    <Table>
      <TableHeader>
        <TableRow className="bg-neutral-50">
          {columns.find((col) => col.id === "order_id")?.isVisible && (
            <TableHead className="h-10 px-2 font-semibold text-neutral-700 whitespace-nowrap">Order ID</TableHead>
          )}
          {columns.find((col) => col.id === "client")?.isVisible && !hideClientColumn && (
            <TableHead className="h-10 px-2 font-semibold text-neutral-700">Client</TableHead>
          )}
          {columns.find((col) => col.id === "business")?.isVisible && (
            <TableHead className="h-10 px-2 font-semibold text-neutral-700">Business</TableHead>
          )}
          {userEmailColumn?.isVisible && (
            <TableHead className="h-10 px-1.5 font-semibold text-neutral-700 whitespace-nowrap w-[7.5rem] max-w-[7.5rem]">
              {userEmailColumn.label}
            </TableHead>
          )}
          {columns.find((col) => col.id === "recurrence")?.isVisible && (
            <TableHead className="h-10 px-2 font-semibold text-neutral-700">Recurrence</TableHead>
          )}
          {columns.find((col) => col.id === "locations")?.isVisible && (
            <TableHead className="h-10 px-2 font-semibold text-neutral-700">Locations</TableHead>
          )}
          {columns.find((col) => col.id === "combo_configuration")?.isVisible && (
            <TableHead className="h-10 px-2 font-semibold text-neutral-700">Combo Configuration</TableHead>
          )}
          {columns.find((col) => col.id === "links_progress")?.isVisible && (
            <TableHead className="h-10 px-2 font-semibold text-neutral-700">Links Progress</TableHead>
          )}
          {columns.find((col) => col.id === "status")?.isVisible && (
            <TableHead className="h-10 px-2 font-semibold text-neutral-700">Status</TableHead>
          )}
          {columns.find((col) => col.id === "dates")?.isVisible && (
            <TableHead className="h-10 px-2 font-semibold text-neutral-700">Dates</TableHead>
          )}
          {columns.find((col) => col.id === "paid")?.isVisible && (
            <TableHead className="h-10 px-2 font-semibold text-neutral-700 text-center w-12" title="Paid status">
              Paid
            </TableHead>
          )}
          {columns.find((col) => col.id === "actions")?.isVisible && (
            <TableHead className="h-10 px-2 font-semibold text-neutral-700">Actions</TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading || paginationLoading ? (
          <TableSkeleton rows={10} columns={columnCount} />
        ) : error ? (
          <TableRow>
            <TableCell colSpan={columnCount} className="px-4 py-8 text-center">
              <div className="flex flex-col items-center gap-3">
                <p className="text-sm text-red-800 font-medium">{error}</p>
                {onRetry && (
                  <Button type="button" variant="outline" size="sm" onClick={onRetry}>
                    Retry
                  </Button>
                )}
              </div>
            </TableCell>
          </TableRow>
        ) : orders.length > 0 ? (
          <>
            {orders.map((order) => {
                const clientName = getClientName(order)
                const businessDisplay = getBusinessName(order)

                const { linkCount, completeCount } = getLinksProgressCounts(order)

                const finalLinkCount = linkCount || safeNumericValue(order.link_count)
                const finalComplete = completeCount || safeNumericValue(order.completed_links)
                const linksPending = finalLinkCount - finalComplete
                const progressPercentage = calculateProgress(finalComplete, finalLinkCount)
                const locations = getOrderLocations(order)
                const combos = getOrderCombos(order)
                const linkStatuses = getLinkStatuses(order)
                const dateStatus = getDateStatus(order.due_date || "")
                const recurrence = getRecurrenceDisplay(order)

                return (
                  <TableRow key={safeStringValue(order.id)} className="hover:bg-neutral-50">
                    {columns.find((col) => col.id === "order_id")?.isVisible && (
                      <TableCell className="p-2">
                        <Link href={orderDetailHref(safeStringValue(order.id))} className="text-blue-600 hover:underline font-medium">
                          {safeStringValue(order.id)}
                        </Link>
                      </TableCell>
                    )}
                    {columns.find((col) => col.id === "client")?.isVisible && !hideClientColumn && (
                      <TableCell className="p-2">
                        {getClientId(order) ? (
                          <Link href={`/clients/${getClientId(order)}`} className="text-neutral-900 hover:text-blue-600">
                            {clientName}
                          </Link>
                        ) : (
                          <span className="text-neutral-900">{clientName}</span>
                        )}
                      </TableCell>
                    )}
                    {columns.find((col) => col.id === "business")?.isVisible && (
                      <TableCell className="p-2">
                        {businessDisplay ? (
                          <Link
                            href={`/businesses/${getBusinessId(order)}`}
                            className="text-xs text-neutral-900 hover:text-blue-600"
                          >
                            {businessDisplay}
                          </Link>
                        ) : (
                          <span className="text-xs text-neutral-400">—</span>
                        )}
                      </TableCell>
                    )}
                    {userEmailColumn?.isVisible && (
                      <TableCell className="p-2 px-1.5 min-w-0 w-[7.5rem] max-w-[7.5rem] overflow-hidden align-middle">
                        {(() => {
                          const emailStr = getOrderLinkedUsersEmails(order)
                          if (!emailStr) return null
                          return (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-xs text-neutral-800 block w-full min-w-0 max-w-full whitespace-nowrap overflow-hidden text-ellipsis cursor-default text-left">
                                  {emailStr}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" align="start" className="max-w-md break-all">
                                {emailStr}
                              </TooltipContent>
                            </Tooltip>
                          )
                        })()}
                      </TableCell>
                    )}
                    {columns.find((col) => col.id === "recurrence")?.isVisible && (
                      <TableCell className="p-2">
                        {recurrence ? (
                          <div className="flex flex-col gap-1.5 items-start">
                            <Badge className="bg-blue-600 text-white px-2 py-0.5 text-xs font-semibold whitespace-nowrap">
                              {recurrence.periodLabel}
                            </Badge>
                            <div className="flex items-center gap-2 text-sm text-neutral-700">
                              <Repeat className="w-3.5 h-3.5 flex-shrink-0" />
                              <div className="flex flex-col gap-0.5">
                                <div className="text-xs font-medium">{recurrence.frequencyLabel}</div>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-neutral-400">—</span>
                        )}
                      </TableCell>
                    )}
                    {columns.find((col) => col.id === "locations")?.isVisible && (
                      <TableCell className="p-2 max-w-[160px]">
                        {locations.length > 0 ? (
                          locations.length <= 3 ? (
                            <div className="flex flex-wrap gap-1.5">
                              {locations.map((location, idx) => (
                                <Badge key={idx} variant="outline" className="text-xs font-normal px-2 py-0.5 whitespace-nowrap">
                                  {location}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className="text-left hover:underline text-sm">
                                  <div className="flex flex-wrap gap-1.5">
                                    {locations.slice(0, 2).map((location, idx) => (
                                      <Badge key={idx} variant="outline" className="text-xs font-normal px-2 py-0.5 whitespace-nowrap">
                                        {location}
                                      </Badge>
                                    ))}
                                    <Badge variant="secondary" className="text-xs font-medium px-2 py-0.5">
                                      +{locations.length - 2} more
                                    </Badge>
                                  </div>
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80 p-3">
                                <div className="space-y-2">
                                  <p className="text-sm font-semibold text-neutral-900">All Locations</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {locations.map((location, idx) => (
                                      <Badge key={idx} variant="outline" className="text-xs font-normal px-2 py-1">
                                        {location}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                          )
                        ) : (
                          <span className="text-neutral-400">—</span>
                        )}
                      </TableCell>
                    )}
                    {columns.find((col) => col.id === "combo_configuration")?.isVisible && (
                      <TableCell className="p-2">
                        <ComboConfigurationCell combos={combos} />
                      </TableCell>
                    )}
                    {columns.find((col) => col.id === "links_progress")?.isVisible && (
                      <TableCell className="p-2">
                        <div className="flex flex-col gap-2 min-w-[140px]">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-neutral-600">
                              {finalComplete}/{finalLinkCount}
                            </span>
                            <span className="font-medium text-neutral-900">{progressPercentage}%</span>
                          </div>
                          <div className="w-full h-1.5 bg-neutral-200 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-600 rounded-full transition-all" style={{ width: `${progressPercentage}%` }} />
                          </div>
                          <div className="flex flex-wrap gap-x-2 gap-y-0.5 text-xs">
                            {linkStatuses.created > 0 && (
                              <span className="flex items-center gap-1.5 text-slate-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-slate-500 flex-shrink-0" />
                                {linkStatuses.created} Created
                              </span>
                            )}
                            {linkStatuses.pendingClientApproval > 0 && (
                              <span className="flex items-center gap-1.5 text-amber-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                                {linkStatuses.pendingClientApproval} Pending Approval
                              </span>
                            )}
                            {linkStatuses.approvedByClient > 0 && (
                              <span className="flex items-center gap-1.5 text-green-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-600 flex-shrink-0" />
                                {linkStatuses.approvedByClient} Approved by Client
                              </span>
                            )}
                            {linkStatuses.complete > 0 && (
                              <span className="flex items-center gap-1.5 text-emerald-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                                {linkStatuses.complete} Complete
                              </span>
                            )}
                            {linkStatuses.live > 0 && (
                              <span className="flex items-center gap-1.5 text-blue-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0" />
                                {linkStatuses.live} live
                              </span>
                            )}
                            {linkStatuses.declinedByClient > 0 && (
                              <span className="flex items-center gap-1.5 text-red-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-600 flex-shrink-0" />
                                {linkStatuses.declinedByClient} Declined by Client
                              </span>
                            )}
                            {linkStatuses.badApple > 0 && (
                              <span className="flex items-center gap-1.5 text-red-700">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-800 flex-shrink-0" />
                                {linkStatuses.badApple} Bad Apple
                              </span>
                            )}
                            {linkStatuses.reserves > 0 && (
                              <span className="flex items-center gap-1.5 text-purple-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-purple-600 flex-shrink-0" />
                                {linkStatuses.reserves} reserves
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            disabled={getActiveConnections(order).length === 0}
                            onClick={() => setLinksModalOrder(order)}
                            className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:no-underline"
                          >
                            <Eye className="w-3 h-3" />
                            View details
                          </button>
                        </div>
                      </TableCell>
                    )}
                    {columns.find((col) => col.id === "status")?.isVisible && (
                      <TableCell className="p-2">
                        <Badge className={`${getClientOrderStatusColor(order.status)} whitespace-nowrap`}>
                          {formatClientOrderStatus(order.status)}
                        </Badge>
                      </TableCell>
                    )}
                    {columns.find((col) => col.id === "dates")?.isVisible && (
                      <TableCell className="p-2">
                        <div className="flex flex-col gap-1 min-w-[120px]">
                          <div className="text-xs text-neutral-500">
                            <span className="font-medium">Created:</span>{" "}
                            {order.date_order || order.created_date ? formatOrderTableDate(order.date_order || order.created_date || "") : "N/A"}
                          </div>
                          <div className="text-xs text-neutral-500">
                            <span className="font-medium">Start:</span>{" "}
                            {order.starts_at
                              ? formatOrderTableDate(order.starts_at)
                              : order.date_order || order.created_date
                                ? formatOrderTableDate(order.date_order || order.created_date || "")
                                : "N/A"}
                          </div>
                          <div
                            className={`text-xs flex items-center gap-1 ${
                              dateStatus.status === "overdue"
                                ? "text-red-600 font-semibold"
                                : dateStatus.status === "approaching"
                                  ? "text-amber-600 font-medium"
                                  : "text-neutral-700"
                            }`}
                          >
                            <span className="font-medium">Due:</span>
                            <span>{order.due_date ? formatOrderDueDate(order.due_date) : "N/A"}</span>
                            {dateStatus.status === "overdue" && <AlertCircle className="w-3 h-3 flex-shrink-0" title={`${dateStatus.daysOverdue} days overdue`} />}
                            {dateStatus.status === "approaching" && (
                              <AlertCircle className="w-3 h-3 flex-shrink-0" title={`${dateStatus.daysRemaining} days remaining`} />
                            )}
                          </div>
                        </div>
                      </TableCell>
                    )}
                    {columns.find((col) => col.id === "paid")?.isVisible && (
                      <TableCell className="p-2 text-center align-middle w-12">
                        <div className="space-y-1">
                          <div className="flex items-center justify-center">
                            <span
                              className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                                order.is_paid
                                  ? "bg-green-100 text-green-700 ring-1 ring-green-200/90"
                                  : "bg-red-100 text-red-700 ring-1 ring-red-200/90"
                              }`}
                              title={order.is_paid ? "Paid" : "Unpaid"}
                              aria-label={order.is_paid ? "Paid" : "Unpaid"}
                              role="img"
                            >
                              {order.is_paid ? (
                                <Check className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
                              ) : (
                                <X className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden />
                              )}
                            </span>
                          </div>
                          {order.is_paid && order.stripe_invoice && (
                            <>
                              <div className="text-xs text-neutral-600">Stripe</div>
                              <a
                                href={order.stripe_invoice.startsWith("http") ? order.stripe_invoice : `#${order.stripe_invoice}`}
                                target={order.stripe_invoice.startsWith("http") ? "_blank" : undefined}
                                rel={order.stripe_invoice.startsWith("http") ? "noopener noreferrer" : undefined}
                                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                              >
                                {order.stripe_invoice.length > 20 ? `${order.stripe_invoice.substring(0, 20)}...` : order.stripe_invoice}
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            </>
                          )}
                        </div>
                      </TableCell>
                    )}
                    {columns.find((col) => col.id === "actions")?.isVisible && (
                      <TableCell className="p-2">
                        <div className="flex items-center gap-1">
                          {showInternalOrderEdit &&
                            (() => {
                              const normalizedStatus = formatClientOrderStatus(order.status).toLowerCase().replace(/\s+/g, "_")
                              const canEdit = !["complete", "cancelled"].includes(normalizedStatus)
                              return canEdit ? (
                                <Link href={`${orderDetailHref(safeStringValue(order.id))}/edit`}>
                                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-neutral-100" title="Edit order">
                                    <Pencil className="w-4 h-4 text-neutral-600" />
                                  </Button>
                                </Link>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled
                                  className="h-8 w-8 p-0"
                                  title="Order cannot be edited when complete or cancelled"
                                >
                                  <Pencil className="w-4 h-4 text-neutral-400" />
                                </Button>
                              )
                            })()}
                          {onDuplicateOrder && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 hover:bg-neutral-100"
                              title="Duplicate order"
                              onClick={() => onDuplicateOrder(order)}
                            >
                              <Files className="w-4 h-4 text-neutral-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
          </>
        ) : (
          <TableRow>
            <TableCell colSpan={columnCount} className="p-2 py-8 text-center text-neutral-500 text-sm">
              {emptyMessage}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
    </TooltipProvider>
    <LinksDetailsModal
      open={!!linksModalOrder}
      onOpenChange={(open) => !open && setLinksModalOrder(null)}
      orderId={linksModalOrder?.id ?? ""}
      connections={getActiveConnections(linksModalOrder ?? {})}
      zsOppFee={linksModalOrder?.zs_opp_fee != null ? Number(linksModalOrder.zs_opp_fee) : 150}
    />
    </>
  )
}
